import { useState, useCallback } from 'react';
import { getWorkFlow } from '../apis/workflow/get/getWorkFlow';
import { getActivities } from '../apis/workflow/get/getActivities';
import { getDocuments } from '../apis/document/get/getDocuments';
import { FormDataFormatted } from '../types/formData.types';
import { PaymentWorkflowType } from '../components/container/forms/types/api/graph.types';
import { AuthContextProps } from '../contexts/AuthContext';
import { sectionKeyToDisplay } from '../types/formData.types';
import { PrintOptions } from '../components/layout/bodyLayout/PrintOptionsModal';
import { Activity } from '../components/container/forms/types/api/graph.types';

interface UseSingleWorkflowPrintParams {
  getAccessToken: AuthContextProps['getAccessToken'];
  spoofedUser: string | null;
  workflowId: string;
  workflowType: PaymentWorkflowType;
}

interface UseSingleWorkflowPrintReturn {
  isPrinting: boolean;
  printWorkflow: (options: PrintOptions) => Promise<void>;
}

/**
 * Hook for printing a single workflow with all its data
 */
export const useSingleWorkflowPrint = ({
  getAccessToken,
  spoofedUser,
  workflowId,
  workflowType,
}: UseSingleWorkflowPrintParams): UseSingleWorkflowPrintReturn => {
  const [isPrinting, setIsPrinting] = useState(false);

  const printWorkflow = useCallback(
    async (options: PrintOptions) => {
      if (!workflowId) {
        alert('No workflow ID provided.');
        return;
      }

      setIsPrinting(true);

      try {
        // Fetch the full workflow data
        const response = await getWorkFlow({
          id: workflowId,
          type: workflowType,
          getAccessToken,
          spoofedUser,
        });

        if (!response.success || !response.data) {
          throw new Error(response.error || 'Failed to fetch workflow data');
        }

        // Handle both single object and array responses
        const workflowData: FormDataFormatted = Array.isArray(response.data)
          ? response.data[0]
          : response.data;

        // Fetch additional data based on options
        let activities: Activity[] = [];
        let documents: unknown[] = [];
        let comments: Activity[] = [];

        if (options.includeActivities || options.includeComments) {
          const activitiesResponse = await getActivities({
            id: workflowId,
            getAccessToken,
          });
          if (activitiesResponse.success && activitiesResponse.data) {
            const allActivities = Array.isArray(activitiesResponse.data)
              ? activitiesResponse.data
              : [activitiesResponse.data];
            activities = allActivities;
            // Filter comments from activities
            comments = allActivities.filter(
              (activity) =>
                activity.details &&
                activity.details.toLowerCase().includes('comment')
            );
          }
        }

        if (options.includeAttachments) {
          const documentsResponse = await getDocuments({
            workflowId: parseInt(workflowId, 10),
            getAccessToken,
            spoofedUser,
          });
          if (documentsResponse.success && documentsResponse.data) {
            documents = Array.isArray(documentsResponse.data)
              ? documentsResponse.data
              : [documentsResponse.data];
          }
        }

        // Create print window
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
          alert('Please allow pop-ups to print workflows.');
          return;
        }

        // Format the date for the print header
        const printDate = new Date().toLocaleString();
        const createdDate = workflowData.createdDate
          ? new Date(workflowData.createdDate).toLocaleString()
          : 'N/A';

        // Format section data for printing
        const formatSectionData = (
          section: FormDataFormatted['sections'][0]
        ) => {
          const sectionDisplayName =
            sectionKeyToDisplay[
              section.sectionKey as keyof typeof sectionKeyToDisplay
            ] || section.sectionKey;
          const isComplete = section.isComplete ? 'Completed' : 'Not Completed';
          const completedDate = section.completedDate
            ? new Date(section.completedDate).toLocaleString()
            : 'N/A';

          // Handle assignments array from API response
          // The API returns assignments array, but FormSection type has assignedToUserId
          const assignments = (
            section as unknown as {
              assignments?: Array<{ displayName?: string; email?: string }>;
            }
          ).assignments;
          const assignedTo =
            assignments && assignments.length > 0
              ? assignments[0].displayName ||
                assignments[0].email ||
                'Unassigned'
              : (section as unknown as { assignedToUserId?: string })
                  .assignedToUserId || 'Unassigned';

          // Helper function to parse attachment JSON and extract file names
          const parseAttachmentField = (value: unknown): string => {
            if (typeof value !== 'string') return String(value || '');
            try {
              const parsed = JSON.parse(value);
              if (Array.isArray(parsed)) {
                return (
                  parsed
                    .map((item: { fileName?: string }) => item.fileName || '')
                    .filter((name: string) => name)
                    .join(', ') || String(value)
                );
              }
              return parsed.fileName || String(value);
            } catch {
              return String(value);
            }
          };

          // Format fields
          let fieldsHtml = '<p><em>No field data</em></p>';
          if (section.fields && Object.keys(section.fields).length > 0) {
            fieldsHtml =
              '<table style="width: 100%; margin-top: 10px;"><tbody>';
            Object.entries(section.fields).forEach(([key, value]) => {
              if (value !== null && value !== undefined && value !== '') {
                const displayKey = key
                  .replace(/_/g, ' ')
                  .replace(/\b\w/g, (l) => l.toUpperCase());
                // Special handling for attachment fields
                const displayValue =
                  key.includes('attachment') ||
                  key.includes('_attachment') ||
                  key.includes('spreadsheet')
                    ? parseAttachmentField(value)
                    : String(value);
                fieldsHtml += `
                <tr>
                  <td style="padding: 4px; font-weight: bold; width: 30%;">${displayKey}:</td>
                  <td style="padding: 4px;">${displayValue}</td>
                </tr>
              `;
              }
            });
            fieldsHtml += '</tbody></table>';
          }

          // Format grid data if present
          // gridData can be an array, an object with keys (like { payment_details: [...] }), or a single object
          // Convert from rows to columns for better readability
          let gridDataHtml = '';
          if (section.gridData) {
            let gridRows: unknown[] = [];

            if (Array.isArray(section.gridData)) {
              gridRows = section.gridData;
            } else if (
              typeof section.gridData === 'object' &&
              section.gridData !== null
            ) {
              // Check if it's an object with array values (like { payment_details: [...] })
              const entries = Object.entries(section.gridData);
              if (entries.length > 0 && Array.isArray(entries[0][1])) {
                // Flatten all arrays from object values
                gridRows = entries.flatMap(([, value]) =>
                  Array.isArray(value) ? value : [value]
                );
              } else {
                // Single object
                gridRows = [section.gridData];
              }
            }

            if (gridRows.length > 0) {
              // Get all unique keys from all rows, excluding location and __rowIndex
              const allKeys = new Set<string>();
              gridRows.forEach((row) => {
                Object.keys(row as Record<string, unknown>).forEach((key) => {
                  if (key !== 'location' && key !== '__rowIndex') {
                    allKeys.add(key);
                  }
                });
              });

              const columns = Array.from(allKeys);

              // Build table with columns as rows (transposed)
              gridDataHtml =
                '<h4 style="margin-top: 15px;">Payment Details:</h4><table style="width: 100%; border-collapse: collapse; margin-top: 10px;"><thead><tr>';
              gridDataHtml += `<th style="border: 1px solid #ddd; padding: 8px; background-color: #f2f2f2; width: 30%;">Field</th>`;
              // Add a column header for each payment row
              for (let i = 0; i < gridRows.length; i++) {
                gridDataHtml += `<th style="border: 1px solid #ddd; padding: 8px; background-color: #f2f2f2;">Payment ${
                  i + 1
                }</th>`;
              }
              gridDataHtml += '</tr></thead><tbody>';

              // For each column (field), create a row
              columns.forEach((columnKey) => {
                const headerName = columnKey
                  .replace(/_/g, ' ')
                  .replace(/\b\w/g, (l) => l.toUpperCase());
                gridDataHtml += '<tr>';
                gridDataHtml += `<td style="border: 1px solid #ddd; padding: 8px; font-weight: bold; background-color: #f9f9f9;">${headerName}</td>`;

                // Add value for each payment row
                gridRows.forEach((row) => {
                  const rowData = row as Record<string, unknown>;
                  const value = rowData[columnKey];
                  gridDataHtml += `<td style="border: 1px solid #ddd; padding: 8px;">${String(
                    value || ''
                  )}</td>`;
                });

                gridDataHtml += '</tr>';
              });

              gridDataHtml += '</tbody></table>';
            }
          }

          return `
          <div class="section" style="page-break-inside: avoid; margin-bottom: 30px; padding: 15px; border: 1px solid #ddd; border-radius: 4px;">
            <h3 style="margin-top: 0; color: #333; border-bottom: 2px solid #0078d4; padding-bottom: 8px;">${sectionDisplayName}</h3>
            <table style="width: 100%; margin-bottom: 15px;">
              <tr>
                <td style="padding: 4px; font-weight: bold; width: 30%;">Status:</td>
                <td style="padding: 4px;">${isComplete}</td>
              </tr>
              <tr>
                <td style="padding: 4px; font-weight: bold;">Completed Date:</td>
                <td style="padding: 4px;">${completedDate}</td>
              </tr>
              <tr>
                <td style="padding: 4px; font-weight: bold;">Assigned To:</td>
                <td style="padding: 4px;">${assignedTo}</td>
              </tr>
            </table>
            ${fieldsHtml}
            ${gridDataHtml}
          </div>
        `;
        };

        // Build sections HTML
        const sectionsHtml = workflowData.sections
          ? workflowData.sections.map(formatSectionData).join('')
          : '<p><em>No sections found</em></p>';

        // Create the print HTML
        const printHtml = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8">
            <title>Workflow ${workflowData.id} - ${
          workflowData.name || 'Untitled'
        }</title>
            <style>
              @page {
                margin: 1.5cm;
              }
              body {
                font-family: Arial, sans-serif;
                font-size: 11px;
                margin: 0;
                padding: 20px;
                color: #000;
                line-height: 1.4;
              }
              .print-header {
                margin-bottom: 30px;
                padding-bottom: 15px;
                border-bottom: 3px solid #0078d4;
              }
              .print-header h1 {
                margin: 0 0 10px 0;
                font-size: 24px;
                color: #0078d4;
              }
              .print-header .workflow-info {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 15px;
                margin-top: 15px;
              }
              .print-header .info-item {
                padding: 8px;
                background-color: #f5f5f5;
                border-radius: 4px;
              }
              .print-header .info-label {
                font-weight: bold;
                color: #666;
                font-size: 10px;
                text-transform: uppercase;
              }
              .print-header .info-value {
                margin-top: 4px;
                font-size: 12px;
              }
              .section {
                page-break-inside: avoid;
                margin-bottom: 30px;
                padding: 15px;
                border: 1px solid #ddd;
                border-radius: 4px;
              }
              .section h3 {
                margin-top: 0;
                color: #333;
                border-bottom: 2px solid #0078d4;
                padding-bottom: 8px;
              }
              table {
                width: 100%;
                border-collapse: collapse;
              }
              th, td {
                border: 1px solid #ddd;
                padding: 8px;
                text-align: left;
              }
              th {
                background-color: #f2f2f2;
                font-weight: bold;
              }
              @media print {
                .no-print {
                  display: none;
                }
                body {
                  padding: 0;
                }
                .section {
                  page-break-inside: avoid;
                }
              }
            </style>
          </head>
          <body>
            <div class="print-header">
              <h1>${workflowData.name || 'Untitled Workflow'}</h1>
              <div class="workflow-info">
                <div class="info-item">
                  <div class="info-label">Workflow ID</div>
                  <div class="info-value">${workflowData.id}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Status</div>
                  <div class="info-value">${
                    workflowData.status &&
                    sectionKeyToDisplay[
                      workflowData.status as keyof typeof sectionKeyToDisplay
                    ]
                      ? sectionKeyToDisplay[
                          workflowData.status as keyof typeof sectionKeyToDisplay
                        ]
                      : workflowData.status || 'N/A'
                  }</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Created By</div>
                  <div class="info-value">${
                    workflowData.createdByName || 'N/A'
                  }</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Created Date</div>
                  <div class="info-value">${createdDate}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Last Modified</div>
                  <div class="info-value">${
                    workflowData.lastModifiedAt
                      ? new Date(workflowData.lastModifiedAt).toLocaleString()
                      : 'N/A'
                  }</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Print Date</div>
                  <div class="info-value">${printDate}</div>
                </div>
              </div>
            </div>
            <h2 style="margin-top: 30px; margin-bottom: 20px; color: #333; border-bottom: 2px solid #0078d4; padding-bottom: 8px;">Workflow Sections</h2>
            ${sectionsHtml}
            ${options.includePeople ? generatePeopleSection(workflowData) : ''}
            ${options.includeComments ? generateCommentsSection(comments) : ''}
            ${
              options.includeAttachments
                ? generateAttachmentsSection(documents)
                : ''
            }
            ${
              options.includeActivities
                ? generateActivitiesSection(activities)
                : ''
            }
          </body>
        </html>
      `;

        printWindow.document.write(printHtml);
        printWindow.document.close();

        // Wait for content to load, then print
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 250);
        };
      } catch (error) {
        console.error('Error printing workflow:', error);
        alert(
          `Failed to print workflow: ${
            error instanceof Error ? error.message : 'An unknown error occurred'
          }`
        );
      } finally {
        setIsPrinting(false);
      }
    },
    [workflowId, workflowType, getAccessToken, spoofedUser]
  );

  return {
    isPrinting,
    printWorkflow,
  };
};

// Helper function to generate people section
const generatePeopleSection = (workflowData: FormDataFormatted): string => {
  const people: Array<{ name: string; role: string; section: string }> = [];

  // Add creator
  if (workflowData.createdByName) {
    people.push({
      name: workflowData.createdByName,
      role: 'Creator',
      section: 'Workflow',
    });
  }

  // Add people from sections
  if (workflowData.sections) {
    workflowData.sections.forEach((section) => {
      const assignments = (
        section as unknown as {
          assignments?: Array<{ displayName?: string; email?: string }>;
        }
      ).assignments;
      const assignedTo =
        assignments && assignments.length > 0
          ? assignments[0].displayName || assignments[0].email || null
          : (section as unknown as { assignedToUserId?: string })
              .assignedToUserId || null;

      if (assignedTo) {
        const sectionDisplayName =
          sectionKeyToDisplay[
            section.sectionKey as keyof typeof sectionKeyToDisplay
          ] || section.sectionKey;
        people.push({
          name: assignedTo,
          role: 'Assigned',
          section: sectionDisplayName,
        });
      }
    });
  }

  if (people.length === 0) {
    return '<p><em>No people information available</em></p>';
  }

  let html = `
      <h2 style="margin-top: 30px; margin-bottom: 20px; color: #333; border-bottom: 2px solid #0078d4; padding-bottom: 8px;">People Working on This Request</h2>
      <table style="width: 100%; border-collapse: collapse; margin-top: 10px;">
        <thead>
          <tr>
            <th style="border: 1px solid #ddd; padding: 8px; background-color: #f2f2f2;">Name</th>
            <th style="border: 1px solid #ddd; padding: 8px; background-color: #f2f2f2;">Role</th>
            <th style="border: 1px solid #ddd; padding: 8px; background-color: #f2f2f2;">Section</th>
          </tr>
        </thead>
        <tbody>
    `;

  people.forEach((person) => {
    html += `
        <tr>
          <td style="border: 1px solid #ddd; padding: 8px;">${person.name}</td>
          <td style="border: 1px solid #ddd; padding: 8px;">${person.role}</td>
          <td style="border: 1px solid #ddd; padding: 8px;">${person.section}</td>
        </tr>
      `;
  });

  html += `
        </tbody>
      </table>
    `;

  return html;
};

// Helper function to generate comments section
const generateCommentsSection = (comments: Activity[]): string => {
  if (comments.length === 0) {
    return `
        <h2 style="margin-top: 30px; margin-bottom: 20px; color: #333; border-bottom: 2px solid #0078d4; padding-bottom: 8px;">Request Comments</h2>
        <p><em>No comments available</em></p>
      `;
  }

  let html = `
      <h2 style="margin-top: 30px; margin-bottom: 20px; color: #333; border-bottom: 2px solid #0078d4; padding-bottom: 8px;">Request Comments</h2>
    `;

  comments.forEach((comment) => {
    const commentDate = comment.timestamp
      ? new Date(comment.timestamp).toLocaleString()
      : 'N/A';
    html += `
        <div style="margin-bottom: 15px; padding: 10px; border-left: 3px solid #0078d4; background-color: #f9f9f9;">
          <div style="font-weight: bold; margin-bottom: 5px;">${
            comment.displayName || 'Unknown'
          }</div>
          <div style="font-size: 10px; color: #666; margin-bottom: 8px;">${commentDate}</div>
          <div>${comment.details || 'No comment text'}</div>
        </div>
      `;
  });

  return html;
};

// Helper function to generate attachments section
const generateAttachmentsSection = (documents: unknown[]): string => {
  if (documents.length === 0) {
    return `
        <h2 style="margin-top: 30px; margin-bottom: 20px; color: #333; border-bottom: 2px solid #0078d4; padding-bottom: 8px;">Request Attachment Files</h2>
        <p><em>No attachment files available</em></p>
      `;
  }

  let html = `
      <h2 style="margin-top: 30px; margin-bottom: 20px; color: #333; border-bottom: 2px solid #0078d4; padding-bottom: 8px;">Request Attachment Files</h2>
      <table style="width: 100%; border-collapse: collapse; margin-top: 10px;">
        <thead>
          <tr>
            <th style="border: 1px solid #ddd; padding: 8px; background-color: #f2f2f2;">File Name</th>
            <th style="border: 1px solid #ddd; padding: 8px; background-color: #f2f2f2;">Upload Date</th>
            <th style="border: 1px solid #ddd; padding: 8px; background-color: #f2f2f2;">Uploaded By</th>
          </tr>
        </thead>
        <tbody>
    `;

  documents.forEach((doc: unknown) => {
    const docData = doc as {
      fileName?: string;
      name?: string;
      uploadedAt?: string;
      createdAt?: string;
      uploadedBy?: string;
      createdBy?: string;
    };
    const fileName = docData.fileName || docData.name || 'Unknown';
    const uploadDate = docData.uploadedAt
      ? new Date(docData.uploadedAt).toLocaleString()
      : docData.createdAt
      ? new Date(docData.createdAt).toLocaleString()
      : 'N/A';
    const uploadedBy = docData.uploadedBy || docData.createdBy || 'Unknown';
    html += `
        <tr>
          <td style="border: 1px solid #ddd; padding: 8px;">${fileName}</td>
          <td style="border: 1px solid #ddd; padding: 8px;">${uploadDate}</td>
          <td style="border: 1px solid #ddd; padding: 8px;">${uploadedBy}</td>
        </tr>
      `;
  });

  html += `
        </tbody>
      </table>
    `;

  return html;
};

// Helper function to generate activities section
const generateActivitiesSection = (activities: Activity[]): string => {
  if (activities.length === 0) {
    return `
        <h2 style="margin-top: 30px; margin-bottom: 20px; color: #333; border-bottom: 2px solid #0078d4; padding-bottom: 8px;">Request Activities</h2>
        <p><em>No activities available</em></p>
      `;
  }

  let html = `
      <h2 style="margin-top: 30px; margin-bottom: 20px; color: #333; border-bottom: 2px solid #0078d4; padding-bottom: 8px;">Request Activities</h2>
      <table style="width: 100%; border-collapse: collapse; margin-top: 10px;">
        <thead>
          <tr>
            <th style="border: 1px solid #ddd; padding: 8px; background-color: #f2f2f2;">Date/Time</th>
            <th style="border: 1px solid #ddd; padding: 8px; background-color: #f2f2f2;">Action</th>
            <th style="border: 1px solid #ddd; padding: 8px; background-color: #f2f2f2;">User</th>
            <th style="border: 1px solid #ddd; padding: 8px; background-color: #f2f2f2;">Details</th>
          </tr>
        </thead>
        <tbody>
    `;

  activities.forEach((activity) => {
    const activityDate = activity.timestamp
      ? new Date(activity.timestamp).toLocaleString()
      : 'N/A';
    html += `
        <tr>
          <td style="border: 1px solid #ddd; padding: 8px;">${activityDate}</td>
          <td style="border: 1px solid #ddd; padding: 8px;">${
            activity.action || 'N/A'
          }</td>
          <td style="border: 1px solid #ddd; padding: 8px;">${
            activity.displayName || activity.email || 'Unknown'
          }</td>
          <td style="border: 1px solid #ddd; padding: 8px;">${
            activity.details || 'N/A'
          }</td>
        </tr>
      `;
  });

  html += `
        </tbody>
      </table>
    `;

  return html;
};
